import { AccountMenu as CrowAccountMenu, MenuItem } from '@crow/ui';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { ConnectionSummary } from '@crowdex/plugin-contracts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { cn } from '@crowdex/ui/lib/utils';
import {
  Blocks,
  ChevronsUpDown,
  Keyboard,
  Link2,
  type LucideIcon,
  Settings,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useRouter } from '@/lib/navigation';
import { CONNECTIONS_CHANGED_EVENT } from '@/lib/plugins/connection-events';
import { CONNECTIONS_PATH, pluginsPath } from '@/lib/plugins/plugin-sections';
import { commandLabel, useShortcutOverrides } from '@/lib/settings-page/keyboard-shortcuts';
import { settingsSectionPath } from '@/lib/settings-page/sections';
import { signOutOfCloud } from '@/lib/sign-out';
import { useCrowlingoAccount } from './crowlingo-account';
import { useShellNavigation } from './shell-navigation';
import { SETTINGS_SHORTCUT, SHORTCUTS_SHORTCUT } from './shell-shortcuts';

/** The accounts of services that must be connected again (their plugin cannot use them). */
export function accountsToReconnect(connections: readonly ConnectionSummary[]): string[] {
  return connections
    .filter(
      (connection) =>
        connection.status === 'needs_reauthorization' || connection.declaration === 'changed',
    )
    .map((connection) => connection.name);
}

/** A preview's account to reconnect, as the boards show it (D3b). */
const PREVIEW_RECONNECT: readonly string[] = ['Google'];

/**
 * The accounts to reconnect, read from the hub and again when they change; a preview's are the
 * boards'.
 */
export function useAccountsToReconnect(enabled: boolean): readonly string[] {
  const { preview } = useShellNavigation();
  const names = useHubAccountsToReconnect(enabled && !preview);
  return preview ? PREVIEW_RECONNECT : names;
}

function useHubAccountsToReconnect(enabled: boolean): string[] {
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const load = () =>
      void apiFetch<{ connections: ConnectionSummary[] }>('/api/connections')
        .then((listing) => {
          if (active) setNames(accountsToReconnect(listing.connections));
        })
        .catch(() => undefined);
    load();
    window.addEventListener(CONNECTIONS_CHANGED_EVENT, load);
    window.addEventListener('focus', load);
    return () => {
      active = false;
      window.removeEventListener(CONNECTIONS_CHANGED_EVENT, load);
      window.removeEventListener('focus', load);
    };
  }, [enabled]);
  return names;
}

/** The user's name, as the menu's header says it: theirs, else their email's ("jonas" → "Jonas"). */
export function accountName(name: string, email: string): string {
  if (name.trim() !== '') return name.trim();
  const local = email.split('@')[0] ?? '';
  return local === '' ? email : local.replace(/^./, (letter) => letter.toUpperCase());
}

export function initials(label: string): string {
  const words = label.split(/[\s@._-]+/).filter(Boolean);
  const letters =
    words.length > 1 ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}` : label.slice(0, 2);
  return letters.toUpperCase();
}

/** The account's avatar, with an amber dot while an account must be reconnected. */
export function AccountAvatar({
  label,
  attention,
  size = 'sm',
}: {
  label: string;
  attention: boolean;
  size?: 'sm' | 'lg';
}) {
  return (
    <span className="relative inline-flex">
      <span
        className={cn(
          'flex items-center justify-center rounded-full bg-control font-semibold text-foreground',
          size === 'sm' ? 'type-caption size-6' : 'size-11 text-sm',
        )}
      >
        {initials(label)}
      </span>
      {attention && (
        <span
          aria-hidden
          className={cn(
            'absolute rounded-full bg-attention ring-2 ring-desktop-sidebar',
            size === 'sm' ? '-top-0.5 -right-0.5 size-2' : 'top-0 right-0 size-3',
          )}
        />
      )}
    </span>
  );
}

/**
 * The account menu (D3b), from the person's row at the sidebar's foot (as in every Crowlingo app)
 * or the rail's avatar: the user's name (their Account), Plugins, Connections (which one to
 * reconnect), Settings ⌘ and Keyboard shortcuts ⌘/.
 */
export function AccountMenu({
  name,
  email,
  profile,
  reconnect,
  className,
  side = 'bottom',
  variant = 'avatar',
}: {
  /** The trigger's box. */
  className?: string;
  /** Where the avatar's menu opens: below it, beside the rail. A row's opens above it. */
  side?: 'bottom' | 'right';
  /** `avatar`: the rail's round button; `row`: the person's row at the sidebar's foot. */
  variant?: 'avatar' | 'row';
  name: string;
  email: string;
  /** Where the account lives ("Crowdex Cloud"). */
  profile: string;
  reconnect: readonly string[];
}) {
  // Its labels show the user's keys.
  useShortcutOverrides();
  const router = useRouter();
  const crowlingo = useCrowlingoAccount();
  const label = accountName(name, email);
  const attention = reconnect.length > 0;
  const row = variant === 'row';
  if (crowlingo !== null)
    return (
      <span className={cn('relative shrink-0', row ? 'flex w-full' : 'inline-flex', className)}>
        <CrowAccountMenu
          align={row || side === 'right' ? 'start' : 'end'}
          current="crowdex"
          onSignOut={signOutOfCloud}
          organization={crowlingo.organization}
          person={{ name: label, email }}
          side={row || side === 'right' ? 'top' : 'bottom'}
          variant={row ? 'row' : 'avatar'}
        >
          <MenuItem
            icon={<UserRound />}
            onSelect={() => router.push(settingsSectionPath('account'))}
          >
            Account · {profile}
          </MenuItem>
          <MenuItem icon={<Blocks />} onSelect={() => router.push(pluginsPath())}>
            Plugins
          </MenuItem>
          <MenuItem
            hint={attention ? `${reconnect.length} to reconnect` : undefined}
            icon={<Link2 />}
            onSelect={() => router.push(CONNECTIONS_PATH)}
          >
            Connections
          </MenuItem>
          <MenuItem
            icon={<Settings />}
            onSelect={() => router.push('/settings')}
            shortcut={commandLabel('settings', SETTINGS_SHORTCUT)}
          >
            Settings
          </MenuItem>
          <MenuItem
            icon={<Keyboard />}
            onSelect={() => router.push(settingsSectionPath('keyboard-shortcuts'))}
            shortcut={commandLabel('keyboard-shortcuts', SHORTCUTS_SHORTCUT)}
          >
            Keyboard shortcuts
          </MenuItem>
        </CrowAccountMenu>
        {attention && (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute size-2 rounded-full bg-attention ring-2 ring-desktop-sidebar',
              row ? 'top-1 left-7' : '-top-0.5 -right-0.5',
            )}
          />
        )}
      </span>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {row ? (
          <button
            aria-label={attention ? `Account, ${reconnect.length} to reconnect` : 'Account'}
            className={cn(
              'flex w-full min-w-0 items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-control',
              className,
            )}
            type="button"
          >
            <AccountAvatar attention={attention} label={name.trim() || email} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-foreground">{label}</span>
              <span className="type-caption block truncate text-muted-foreground">{profile}</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
          </button>
        ) : (
          <button
            aria-label={attention ? `Account, ${reconnect.length} to reconnect` : 'Account'}
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            title={label}
            type="button"
          >
            <AccountAvatar attention={attention} label={name.trim() || email} />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={row ? 'start' : 'end'}
        className="w-[276px] p-1.5"
        side={row ? 'top' : side}
        sideOffset={8}
      >
        {/* The name is the Account: Settings › Account. */}
        <DropdownMenuItem
          className="min-h-11 gap-3"
          onSelect={() => router.push(settingsSectionPath('account'))}
        >
          <AccountAvatar attention={false} label={name.trim() || email} />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold">{label}</span>
            <span className="type-caption block truncate text-muted-foreground">
              Account · {profile}
            </span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <AccountItem
          detail="Apps, editors and services"
          icon={Blocks}
          label="Plugins"
          onSelect={() => router.push(pluginsPath())}
        />
        <AccountItem
          attention={attention}
          detail={
            attention
              ? `${reconnect[0]}${reconnect.length > 1 ? ` and ${reconnect.length - 1} more` : ''} ${reconnect.length > 1 ? 'need' : 'needs'} to reconnect`
              : 'Google, GitHub, Slack and your other services'
          }
          icon={Link2}
          label="Connections"
          onSelect={() => router.push(CONNECTIONS_PATH)}
        />
        <DropdownMenuSeparator />
        <AccountItem
          icon={Settings}
          label="Settings"
          onSelect={() => router.push('/settings')}
          shortcut={commandLabel('settings', SETTINGS_SHORTCUT)}
        />
        <AccountItem
          icon={Keyboard}
          label="Keyboard shortcuts"
          onSelect={() => router.push(settingsSectionPath('keyboard-shortcuts'))}
          shortcut={commandLabel('keyboard-shortcuts', SHORTCUTS_SHORTCUT)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountItem({
  icon: Icon,
  label,
  detail,
  shortcut,
  attention = false,
  onSelect,
}: {
  icon: LucideIcon;
  label: string;
  detail?: string;
  shortcut?: string;
  attention?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem className="min-h-10 gap-3" onSelect={onSelect}>
      <Icon />
      <span className="min-w-0 flex-1">
        <span className="block">{label}</span>
        {detail !== undefined && (
          <span
            className={cn(
              'type-caption block truncate',
              attention ? 'text-attention' : 'text-muted-foreground',
            )}
          >
            {detail}
          </span>
        )}
      </span>
      {shortcut !== undefined && (
        <span className="type-caption rounded border border-hairline px-1 text-muted-foreground">
          {shortcut}
        </span>
      )}
    </DropdownMenuItem>
  );
}
